import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { fetchDepartmentsPaginated, updateUser } from 'services/registration';
import { getCourseData } from 'services/elearning';

import { schemaUpdateUser } from './validation';
import { useTranslation } from 'react-i18next';
import { PATHS } from 'constants/routes';
import { useNavigate } from 'react-router-dom';

export interface UpdateUserRequest {
    first_name: string;
    last_name: string;
    phone?: string;
    workplace_name: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_postcode: string;
    employee_position_name?: string;
    department_id?: number;
    email: string;
    workplace_id: number;
}

export const useProfilUpdate = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userData = (() => {
        const raw = localStorage.getItem('userData');
        return raw ? JSON.parse(raw) : {};
    })();
    
    const {
        register: registerUpdate,
        handleSubmit: handleSubmitUpdate,
        formState: { errors: updateErrors },
        setError: setErrorUpdate,
        reset,
        control,
        setValue: setValueUpdate,
    } = useForm<UpdateUserRequest>({
        resolver: yupResolver(schemaUpdateUser),
        defaultValues: {
            first_name: userData?.first_name || '',
            last_name: userData?.last_name || '',
            phone: userData?.phone || '000000000',
            workplace_name: userData?.workplace_name || '',
            workplace_address_city: userData?.workplace_address_city || '',
            workplace_address_street: userData?.workplace_address_street || '',
            workplace_address_number: userData?.workplace_address_number || '',
            workplace_address_postcode: userData?.workplace_address_postcode || '',
            employee_position_name: userData?.employee_position_name,
            department_id: userData?.department_id || '',
            email: userData?.email || '',
            workplace_id: userData?.workplace_id,
        },
    });

    const { data: allCoursesResponse } = useQuery(['allCoursesForProfile'], () => getCourseData(), { staleTime: Infinity });
    const allCourses = allCoursesResponse?.data ?? [];

    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(
        userData?.department_id && userData?.department
            ? { label: typeof userData.department === 'object' ? (userData.department as any)?.name : userData.department, value: userData.department_id }
            : null
    );

    const loadDepartmentOptions = async (search: string, _: any, { page }: { page: number }) => {
        if (!userData?.workplace_id) return { options: [], hasMore: false, additional: { page: 1 } };
        const res = await fetchDepartmentsPaginated(userData.workplace_id, page);
        const options = res.data.map((d: any) => ({ label: d.name, value: d.id }));
        if (page === 1 && userData?.department_id && !selectedDepartment) {
            const match = options.find((o: any) => o.value === userData.department_id);
            if (match) {
                setSelectedDepartment(match);
                setValueUpdate('department_id', match.value);
            }
        }
        return {
            options,
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleDepartmentChange = (option: { label: string; value: number } | null) => {
        setSelectedDepartment(option);
        setValueUpdate('department_id', option?.value ?? null);
    };

    const mutationUpdate = useMutation(updateUser, {
        onSuccess: (res) => {
            toast.success(t('registration.successUpdated'));
            reset(res.data);
            const firstCoursePath = allCourses?.[0]?.path;
            const redirectPath = firstCoursePath ? `${PATHS.ONLINE_TRAININGS}/${firstCoursePath}` : PATHS.HAND_HYGIENE;
            navigate(redirectPath);
        },
        onError: (error: any) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setErrorUpdate(key as keyof UpdateUserRequest, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const onSubmitUpdate = ({ department_id, ...rest }: UpdateUserRequest) => {
        if (!rest.employee_position_name) {
            rest.employee_position_name = 'stanowisko';
        }
        const payload: any = { ...rest };
        if (department_id != null) payload.department_id = department_id;
        mutationUpdate.mutate(payload);
    };

    return {
        registerUpdate,
        handleSubmitUpdate,
        setErrorUpdate,
        onSubmitUpdate,
        updateErrors,
        t,
        control,
        setValueUpdate,
        selectedDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
    };
};